"use client";

import type { LanguagePreference } from "@fluentry/domain";
import {
  Button,
  Checkbox,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@fluentry/ui";
import { ArrowLeftRight } from "lucide-react";
import { LANGUAGE_OPTIONS } from "./language-preferences";

type LanguageControlsProps = {
  nativeLanguage: LanguagePreference;
  targetLanguage: LanguagePreference;
  followInputLanguage: boolean;
  canSwap: boolean;
  onNativeLanguageChange: (value: LanguagePreference) => void;
  onTargetLanguageChange: (value: LanguagePreference) => void;
  onFollowInputLanguageChange: (value: boolean) => void;
  onSwap: () => void;
};

export function LanguageControls({
  nativeLanguage,
  targetLanguage,
  followInputLanguage,
  canSwap,
  onNativeLanguageChange,
  onTargetLanguageChange,
  onFollowInputLanguageChange,
  onSwap,
}: LanguageControlsProps) {
  return (
    <div className="grid gap-2 rounded-[1.1rem] border border-[#0f766e]/8 bg-white/62 p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-[10px] font-semibold tracking-[0.22em] text-[#5f7c80] uppercase">
          Languages
        </span>
        <label className="flex min-h-10 items-center gap-2 text-[11px] font-medium text-[#31575b]">
          {/* The toggle keeps source-follow available without competing with the editor for vertical space. */}
          <Checkbox
            checked={followInputLanguage}
            className="border-[#0f766e]/20"
            onCheckedChange={(checked) => onFollowInputLanguageChange(Boolean(checked))}
          />
          <span>Follow input</span>
        </label>
      </div>
      <div className="grid gap-2 md:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] md:items-end">
        <LanguageSelect
          label="Your language"
          value={nativeLanguage}
          disabled={followInputLanguage}
          onChange={onNativeLanguageChange}
        />
        <div className="flex items-end justify-center md:pb-0.5">
          <Button
            type="button"
            size="icon"
            variant="outline"
            disabled={!canSwap}
            className="h-10 w-10 rounded-full border-[#0f766e]/10 bg-[#eef3f1] text-[#31575b] shadow-none hover:bg-white"
            onClick={onSwap}
            aria-label="Swap your language and output language"
          >
            <ArrowLeftRight className="h-4 w-4" />
          </Button>
        </div>
        <LanguageSelect
          label="Output language"
          value={targetLanguage}
          onChange={onTargetLanguageChange}
        />
      </div>
    </div>
  );
}

function LanguageSelect(props: {
  label: string;
  value: LanguagePreference;
  disabled?: boolean;
  onChange: (value: LanguagePreference) => void;
}) {
  return (
    <label className={`grid gap-1.5 ${props.disabled ? "opacity-60" : ""}`}>
      <span className="text-[10px] font-semibold tracking-[0.22em] text-[#5f7c80] uppercase">
        {props.label}
      </span>
      <div>
        {/* These selectors should feel like the rest of the design system, so language steering uses the shared shadcn-derived primitives instead of native browser chrome. */}
        <Select
          value={props.value}
          disabled={props.disabled}
          onValueChange={(value) => props.onChange(value as LanguagePreference)}
        >
          <SelectTrigger className="h-10 w-full rounded-[0.9rem] border-[#0f766e]/10 bg-white px-3.5 text-sm font-medium text-slate-900 shadow-none data-[placeholder]:text-[#89a0a2]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="rounded-2xl border border-[#0f766e]/10 bg-white p-1 shadow-xl shadow-[#aabfc0]/20">
            <SelectGroup>
              {LANGUAGE_OPTIONS.map((option) => (
                <SelectItem
                  key={option.value}
                  value={option.value}
                  className="min-h-11 rounded-xl px-3 py-2.5 text-sm font-medium text-slate-800 focus:bg-[#eef6f4] focus:text-slate-950"
                >
                  {option.label}
                </SelectItem>
              ))}
            </SelectGroup>
          </SelectContent>
        </Select>
      </div>
    </label>
  );
}
